import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonCompactCard } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "Can you have depression and anxiety at the same time?",
    answer:
      "Yes. Somewhere around half to two-thirds of people with major depression also meet criteria for an anxiety disorder. Depression and anxiety share genetics, brain circuits, and triggers, and each one makes the other harder to treat. Having both at once is the most common presentation of depression, not an unusual case.",
  },
  {
    question: "Why does anxiety make depression harder to treat?",
    answer:
      "Anxious depression tends to respond less well to standard antidepressants. People with both conditions are more likely to have residual symptoms after a medication trial, more likely to relapse, and more likely to need additional medications just to get through the worst days. The anxiety can also hide the depression from the people around you.",
  },
  {
    question: "Can TMS help anxiety if I'm being treated for depression?",
    answer:
      "The brain region that regulates mood — the dorsolateral prefrontal cortex — also helps put the brakes on the brain's worry system. When TMS stimulates that underactive region, patients frequently report that their anxiety eases alongside their mood. This is a well-documented pattern of anxiety improving as depression resolves, not a separate anxiety treatment.",
  },
  {
    question: "Is SAINT FDA-cleared for anxiety?",
    answer:
      "No. SAINT is FDA-cleared for major depressive disorder in adults who have not achieved satisfactory improvement from prior antidepressant medication. The anxiety relief some patients experience is a secondary effect of treating the depression, not a standalone indication. If your anxiety exists without significant depression, a different approach may fit better, and we will tell you so.",
  },
  {
    question: "Do I have to stop my medication to do SAINT?",
    answer:
      "No. Most patients continue their existing medication regimen through treatment. SAINT is not a replacement for what you've built — it is a different kind of intervention aimed at the brain circuitry your medications may not have reached. Never change medication without guidance from your prescribing provider.",
  },
  {
    question: "What if SAINT isn't right for me?",
    answer:
      "A responsible evaluation may determine that another issue should be addressed first or that SAINT is not the right fit. If that is the case, we will tell you honestly and discuss what other options may be appropriate.",
  },
];

const related: RelatedLink[] = [
  {
    title: "Anxiety Chest Tightness: When to Worry",
    href: "/learn/anxiety-chest-tightness-when-to-worry",
    description: "Why anxiety tightens the chest and when it's an emergency.",
  },
  {
    title: "Why Is My Anxiety Worse in the Morning?",
    href: "/learn/why-is-my-anxiety-worse-in-the-morning",
    description: "Morning cortisol, sleep, and simple fixes for morning dread.",
  },
  {
    title: "When Anxiety, Insomnia, and Depression Overlap",
    href: "/depression-anxiety-insomnia",
    description:
      "Why treating the whole pattern matters when symptoms travel together.",
  },
  {
    title: "Anxiety Nausea: Why It Happens & How to Stop It",
    href: "/anxiety-nausea-why-it-happens-how-to-stop-it",
    description: "Why anxiety upsets your stomach and how to calm it.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function DepressedAndAnxious() {
  return (
    <DepressionContentPage
      seo={{
        title: "Depressed and Anxious at the Same Time? | Neuro Wellness",
        description:
          "Depression and anxiety often overlap. Learn why the combination is typical, why it's harder to treat, and how targeting shared brain circuits with SAINT TMS may help.",
        canonical: "/depressed-and-anxious-at-the-same-time",
        keywords:
          "depression and anxiety, anxious depression, TMS for anxiety and depression, SAINT TMS, overlapping depression anxiety, treatment-resistant depression",
      }}
      badge="Clinical Insights"
      heroTitle="Depressed and Anxious at the Same Time? You're Not Confused. You're Typical."
      heroSubtitle="It doesn't feel possible to feel both at once — heavy and slow, fast and sharp. But if you've lived it, you already know that the combination is the most common presentation of depression there is."
      lastUpdated="2026-11-03"
      sections={[
        {
          heading: "How often they overlap",
          body: (
            <div className="space-y-4">
              <p>
                Here is something that surprises a lot of people in their first
                psychiatric appointment: they came in to talk about depression,
                and they leave with a second diagnosis they didn't expect.
                Anxiety.
              </p>
              <p>
                It doesn't feel like it should be possible to feel both.
                Depression is heavy and slow; you can't get off the couch.
                Anxiety is fast and sharp; you can't stop your thoughts. How can
                one person be both at once?
              </p>
              <p>
                But if you've lived it, you already know the answer. You lie in
                bed unable to get up, and at the same time your mind is racing
                through every way the day could go wrong. You have no energy to
                do anything, and you're too restless to sit still. You feel
                nothing about the things you used to love, and you feel dread
                about everything else.
              </p>

              {/* Photo: Depressed and anxious at the same time */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/747c750f-63f9-4095-bedb-11b000e88a04.png"
                  alt="Depressed and anxious at the same time: mind racing while energy is running low"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>

              <p>
                That's not a contradiction. That's the most common presentation
                of depression there is.
              </p>
              <p>
                Somewhere around half to two-thirds of people with major
                depression also meet criteria for an anxiety disorder, most
                often generalized anxiety disorder. The reverse is also true.
                Anxiety and depression share genetics, share brain circuits, and
                share triggers. Chronic stress feeds both. Poor sleep worsens
                both. And each one makes the other harder to treat.
              </p>
              <p>
                If your intake paperwork showed a moderate score on both a
                depression screen (the PHQ-9) and an anxiety screen (the GAD-7),
                you're not an unusual case. You're the standard one.
              </p>

              {/* Infographic: When depression and anxiety overlap */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/af6dfe67-7cbe-4377-bc66-ccf445ead001.png"
                  alt="When depression and anxiety overlap: Depression traits, shared experiences like sleep problems and irritability, and anxiety traits"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          ),
        },
        {
          heading: "Why the combination is harder to treat",
          body: (
            <div className="space-y-4">
              <p>
                Clinicians pay attention to anxious depression for a practical
                reason: it tends to respond less well to standard
                antidepressants. People with both conditions are more likely to
                have residual symptoms after a medication trial, more likely to
                relapse, and more likely to end up with an "as needed"
                anti-anxiety medication on top of their antidepressant just to
                get through the worst days.
              </p>
              <p>
                The anxiety also tends to hide the depression from other people.
                If you're worried, tense, and irritable, the people around you
                see someone who's stressed, not someone who's depressed. You may
                have spent years being told to relax or take a vacation, when
                what you actually needed was treatment.
              </p>

              {/* Infographic: How both can feed each other */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/4d09f45b-81c6-4f39-a452-4be7a430f167.png"
                  alt="How both can feed each other cycle: 1. Worry and tension, 2. Poor sleep and mental overload, 3. Low energy and low mood, 4. Withdrawal or avoidance"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          ),
        },
        {
          heading: "What the overlap looks like day to day",
          body: (
            <div className="space-y-4">
              <p>People with anxious depression often describe:</p>
              <ul className="list-disc pl-6 space-y-2 marker:text-accent">
                <li>Insomnia that's driven by worry, not just low mood</li>
                <li>
                  Irritability that surprises them and the people they love
                </li>
                <li>
                  Trouble concentrating because their mind won't settle on one
                  thing
                </li>
                <li>
                  Physical symptoms: headaches, stomach problems, muscle
                  tension, a dry mouth from stress or from medication
                </li>
                <li>
                  A sense that something bad is about to happen, without being
                  able to say what
                </li>
                <li>Exhaustion that doesn't improve with rest</li>
              </ul>

              {/* Infographic: What it can look like day to day */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/eb1c8887-f56c-4a19-ae98-4b9d261a12a1.png"
                  alt="What it can look like day to day: Overthinking, feeling on edge, sleep trouble, saying I'm fine while struggling, pulling away, difficulty focusing"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>

              <p>
                If you've been treating the depression and wondering why you
                still feel wired and on edge, the anxiety piece may be the
                reason the whole picture hasn't resolved.
              </p>
            </div>
          ),
        },
        {
          heading: "Why a circuit-level treatment matters here",
          body: (
            <div className="space-y-4">
              <p>
                Here's the part most people never hear explained. Depression and
                anxiety don't just share symptoms; they share hardware. The
                region behind your forehead called the dorsolateral prefrontal
                cortex is responsible for regulating mood, and it's also
                responsible for putting the brakes on the brain's threat and
                worry system. When that region is underactive, two things happen
                at once: mood drops, and the worry system runs unchecked.
              </p>
              <p>
                Medication changes the chemical environment those circuits sit
                in. It doesn't directly retrain the circuit. That's one reason
                someone can be on a high dose of an SSRI, plus a sleep aid, plus
                a benzodiazepine, and still feel both flat and on edge.
              </p>
              <p>
                Transcranial magnetic stimulation (TMS) works differently. It
                uses focused magnetic pulses to stimulate that underactive
                region directly, with no anesthesia and nothing entering your
                bloodstream. And because the target sits at the intersection of
                the mood network and the worry network, patients treated for
                depression frequently report that their anxiety eases alongside
                their mood.
              </p>
            </div>
          ),
        },
        {
          heading: "What SAINT adds",
          body: (
            <div className="space-y-4">
              <div className="my-6 grid gap-4 sm:grid-cols-2 not-prose">
                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                    <img
                      src="https://vibe.filesafe.space/1787327131284421342/attachments/61fd8a15-3767-464e-99b6-5a1351dea1b3.png"
                      alt="Personalized fMRI brain scan targeting for SAINT® TMS therapy"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3.5">
                    <p className="font-headings text-sm font-bold text-primary">
                      Personalized fMRI Brain Targeting
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Maps individual circuits where depression and anxiety
                      overlap before treatment.
                    </p>
                  </div>
                </div>

                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                    <img
                      src="https://vibe.filesafe.space/1787327131284421342/attachments/9907b0c5-c62d-4096-ba03-9d7a9cc3ffc5.png"
                      alt="Patient seated comfortably in SAINT® treatment suite during stimulation series"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3.5">
                    <p className="font-headings text-sm font-bold text-primary">
                      Five-Day Stimulation Series
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Ten 10-minute sessions per day with 50-minute breaks;
                      awake and alert throughout.
                    </p>
                  </div>
                </div>
              </div>

              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an accelerated, FDA-cleared form of TMS developed at
                Stanford's Brain Stimulation Lab. It's indicated for adults with
                major depressive disorder who haven't gotten adequate relief
                from antidepressants in their current episode, which describes a
                large share of people with anxious depression.
              </p>
              <p>
                What makes it especially relevant when depression and anxiety
                are tangled together is precision. Standard TMS finds its target
                using external measurements of the head. SAINT uses a functional
                MRI of your own brain. Here's how it works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-bold text-accent underline underline-offset-2 hover:text-accent/80"
                >
                  Verified SAINT® Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-6 space-y-3 marker:text-accent marker:font-bold">
                <li>
                  <strong>Consultation (Zoom web conference).</strong> You meet
                  with our clinical team over a convenient Zoom call. As your
                  evaluation evolves, you will also have the option to meet with
                  our psychiatrist by Zoom web conference or in person at our
                  clinic. You meet with our clinical team to review your
                  depression and anxiety symptoms, your medication history, and
                  whether SAINT is right for you.
                </li>
                <li>
                  <strong>fMRI brain scan (mandatory in person).</strong> A
                  roughly 45-minute functional MRI maps how your individual
                  brain networks are wired and where they're communicating
                  poorly. (Mandatory in-person scan at an imaging partner
                  center.)
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location where
                  stimulation will most effectively reach the mood-regulation
                  network. Neuronavigation guides the coil to that spot for
                  every session.
                </li>
                <li>
                  <strong>
                    Five-day stimulation series (mandatory in person).
                  </strong>{" "}
                  In-person treatment Monday through Friday at our Coral Springs
                  clinic. Ten 10-minute sessions per day, with 50-minute breaks
                  in between, for five consecutive days. Five clinic visits,
                  compared with 30 to 36 for conventional TMS. Most patients
                  notice a change in mood within the first couple of days.
                </li>
              </ol>

              {/* Private Suites Lounge Experience */}
              <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft not-prose my-6">
                <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/7ab7518d-d9c8-4417-b2f2-05299d3b1f3b.png"
                    alt="Private suite with work desk, orange couch, smart TV, and kitchen dining area in the SAINT Lounge Wing"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="font-headings text-sm font-bold text-primary">
                    Private Suites &amp; Dedicated SAINT® Lounge Wing
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Designed for anxious patients who need a calm, soothing
                    environment. Between sessions, you retreat to your own
                    private suite with quiet workstations, Keurig coffee,
                    refrigerator, and streaming TV. Have lunch in peace while
                    our on-site care team ensures everything is running
                    smoothly. Loved ones and companions are always welcome.
                  </p>
                </div>
              </div>
            </div>
          ),
        },
        {
          heading: "What the results look like",
          body: (
            <div className="space-y-4">
              <SaintComparisonCompactCard />
              <p>
                In the clinical trials behind SAINT's FDA clearance,
                participants had treatment-resistant depression and had already
                failed prior treatments. Seventy-nine percent reached remission,
                not just improvement, with an average time to remission of about
                two and a half days. Across more than 400 trial patients, 85
                percent experienced symptom relief, with no serious adverse
                events.
              </p>
              <p>
                Side effects were generally mild: headache, fatigue, and
                temporary scalp discomfort at the treatment site. A small number
                of participants reported transient anxiety during treatment,
                which is worth knowing if anxiety is already part of your
                picture; your provider will monitor for it. SAINT isn't
                appropriate for people with non-removable metal in or near the
                head or an implanted device, and you'll be screened for these at
                your consultation.
              </p>
              <p>
                One honest note: SAINT is FDA-cleared for major depressive
                disorder, not for anxiety disorders as a standalone diagnosis.
                What we're describing is the well-documented pattern of anxiety
                improving as depression resolves, not a separate anxiety
                treatment. If your anxiety exists without significant
                depression, a different approach may fit better, and we'll say
                so.
              </p>
            </div>
          ),
        },
        {
          heading: "What treatment doesn't require you to do",
          body: (
            <div className="space-y-4">
              <p>
                You don't have to figure out which condition is "primary." You
                don't have to stop your medication; most patients continue their
                existing regimen through treatment. You don't have to prove that
                your anxiety is real or that your depression is bad enough. Both
                are real, both are affecting your life, and both are worth
                treating.
              </p>

              {/* Infographic: Support can address both */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/fab8d966-995a-4291-805b-1a5837ae6c9a.png"
                  alt="Support can address both: 1. Evaluation, 2. Therapy or medication review, 3. TMS options, 4. SAINT when appropriate, 5. Follow-up care"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          ),
        },
        {
          heading: "What to do next",
          variant: "callout",
          callout:
            "If you've been carrying both of these at once and feeling like your treatment has only addressed half of it, a consultation is a reasonable next step. At Neuro Wellness TMS Centers of America, the first appointment is an evaluation, not a commitment. We'll look at your full picture: your depression symptoms, your anxiety symptoms, your medication history, and how the two interact. We'll tell you plainly whether SAINT is likely to help, and what the process would look like from your first scan to your final session. Insurance coverage for SAINT varies by plan; our team can help you understand your options, including prior authorization and self-pay. Schedule a consultation: 954-827-9793.",
        },
      ]}
      faq={faqs}
      faqHeading="Questions About Depression, Anxiety & SAINT"
      relatedLinks={related}
      finalCtaHeading="You Don't Have to Pick Which One to Treat First."
      finalCtaText="If depression and anxiety have been tangled together and your treatment has only addressed half of it, speak with our team about whether SAINT may be appropriate for you."
      finalCtaPrimaryLabel="Schedule a Free Consultation"
      finalCtaPrimaryHref="/saint"
      finalCtaSecondaryLabel="Call 954-827-9793"
      finalCtaSecondaryHref="tel:9548279793"
    />
  );
}
